import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Terminal, Lock, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

// Reset-password page — consumes a `?token=...` from the email link.
// On success we log the user in and route them onward (onboarding if needed,
// dashboard otherwise).
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setErr("Missing reset token — request a new reset link.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (pw !== pw2)   { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      setUser(data);
      setDone(true);
      setTimeout(() => {
        nav(data.onboarded ? "/dashboard" : "/onboarding");
      }, 1200);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100 flex flex-col">
      <Link to="/" className="flex items-center gap-2 px-6 py-5 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-[#00E5FF] transition-colors">
        <Terminal size={14} /> hypexlabs · CCNA lab practice
      </Link>
      <div className="flex items-center justify-center p-6 flex-1">
        <div className="w-full max-w-sm">
          {done ? (
            <div className="space-y-5" data-testid="reset-done">
              <CheckCircle2 className="text-[#00FF41]" size={36} />
              <h1 className="font-display font-black text-3xl tracking-tight">Password updated</h1>
              <p className="text-zinc-400 text-sm font-mono">Logging you in…</p>
            </div>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono mb-2">choose a new password</div>
              <h1 className="font-display font-black text-3xl tracking-tight mb-6">Reset password</h1>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
                <div>
                  <label className="text-xs text-zinc-400 uppercase tracking-widest font-mono mb-1 block">new password</label>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                    <input
                      type="password" required minLength={6}
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] px-9 py-2.5 text-sm outline-none font-mono"
                      placeholder="••••••••"
                      data-testid="reset-pw"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-400 uppercase tracking-widest font-mono mb-1 block">confirm password</label>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                    <input
                      type="password" required minLength={6}
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] px-9 py-2.5 text-sm outline-none font-mono"
                      placeholder="••••••••"
                      data-testid="reset-pw-confirm"
                    />
                  </div>
                </div>
                {err && (
                  <div className="flex items-center gap-2 text-xs text-[#EF4444] font-mono" data-testid="reset-error">
                    <AlertCircle size={12} /> {err}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={busy || !token}
                  className="w-full py-2.5 bg-[#00E5FF] hover:bg-[#00b8cc] text-black text-xs font-mono uppercase tracking-widest font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="reset-submit"
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
                  {busy ? "updating…" : "set new password"}
                </button>
              </form>
              <Link to="/login" className="block text-center text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-[#00E5FF] pt-4">
                back to login
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
